// SPDX-License-Identifier: AGPL-3.0-or-later

import * as VoiceSettingsCommands from '@app/features/voice/commands/VoiceSettingsCommands';
import {voiceDeviceManager} from '@app/features/voice/utils/VoiceDeviceManager';
import VoiceSettings from '@app/features/voice/state/VoiceSettings';
import type {
	DesktopAudioDevicePreference,
	DesktopMediaPreferenceKey,
	DesktopMediaPreferencesAPI,
	DesktopMediaPreferencesSnapshot,
	DesktopScreenShareCodecPreference,
} from '@app/types/electron.d';

let initialization: Promise<DesktopMediaPreferencesSnapshot | null> | null = null;

function getApi(): DesktopMediaPreferencesAPI | null {
	return globalThis.window?.electron?.mediaPreferences ?? null;
}

function toDevicePreference(deviceId: string): DesktopAudioDevicePreference {
	return deviceId && deviceId !== 'default' ? {mode: 'device', deviceId} : {mode: 'system-default'};
}

function fromDevicePreference(preference: DesktopAudioDevicePreference): string {
	return preference.mode === 'device' ? preference.deviceId : 'default';
}

function toSupportedCodec(value: string): DesktopScreenShareCodecPreference {
	return value === 'h264' || value === 'h265' || value === 'av1' || value === 'vp9' ? value : 'auto';
}

function applySnapshot(snapshot: DesktopMediaPreferencesSnapshot): void {
	const codec = snapshot.preferences.screenShareCodec;
	VoiceSettingsCommands.update({
		inputDeviceId: fromDevicePreference(snapshot.preferences.audioInput),
		outputDeviceId: fromDevicePreference(snapshot.preferences.audioOutput),
		preferredScreenShareCodec: codec,
		...(codec === 'av1' ? {screenShareAv1OptIn: true} : {}),
		...(codec === 'h265' ? {screenShareHevcOptIn: true} : {}),
	});
}

export function initializeDesktopMediaPreferences(): Promise<DesktopMediaPreferencesSnapshot | null> {
	if (initialization) return initialization;
	const pendingInitialization = (async () => {
		const api = getApi();
		if (!api) return null;
		await VoiceSettings.waitUntilInitialized();
		let snapshot = await api.get();
		if (!snapshot.configured) {
			snapshot = await api.update({
				screenShareCodec: toSupportedCodec(VoiceSettings.getPreferredScreenShareCodec()),
				audioInput: toDevicePreference(VoiceSettings.getInputDeviceId()),
				audioOutput: toDevicePreference(VoiceSettings.getOutputDeviceId()),
			});
		}
		applySnapshot(snapshot);
		return snapshot;
	})();
	initialization = pendingInitialization.catch(() => {
		initialization = null;
		return null;
	});
	return initialization;
}

export function watchDesktopMediaPreferenceDevices(): () => void {
	const api = getApi();
	if (!api) return () => {};
	let active = true;
	const refreshDevices = async () => {
		try {
			await initializeDesktopMediaPreferences();
			const state = await voiceDeviceManager.ensureDevices({requestPermissions: false, forceRefresh: true});
			const snapshot = await api.get();
			if (!active) return;
			const patch: {inputDeviceId?: string; outputDeviceId?: string} = {};
			const inputPreference = snapshot.preferences.audioInput;
			if (inputPreference.mode === 'system-default') {
				patch.inputDeviceId = 'default';
			} else if (state.inputDevices.length > 0) {
				patch.inputDeviceId = state.inputDevices.some((device) => device.deviceId === inputPreference.deviceId)
					? inputPreference.deviceId
					: 'default';
			}
			const outputPreference = snapshot.preferences.audioOutput;
			if (outputPreference.mode === 'system-default') {
				patch.outputDeviceId = 'default';
			} else if (state.outputDevices.length > 0) {
				patch.outputDeviceId = state.outputDevices.some((device) => device.deviceId === outputPreference.deviceId)
					? outputPreference.deviceId
					: 'default';
			}
			if (
				(patch.inputDeviceId !== undefined && patch.inputDeviceId !== VoiceSettings.getInputDeviceId()) ||
				(patch.outputDeviceId !== undefined && patch.outputDeviceId !== VoiceSettings.getOutputDeviceId())
			) {
				VoiceSettingsCommands.update(patch);
			}
		} catch {
			// Device refresh failures leave the existing audio path in place.
		}
	};
	const unsubscribe = api.onDeviceChange(() => void refreshDevices());
	void refreshDevices();
	return () => {
		active = false;
		unsubscribe();
	};
}

export async function updateDesktopAudioDevicePreference(
	kind: 'input' | 'output',
	deviceId: string,
): Promise<DesktopMediaPreferencesSnapshot | null> {
	const api = getApi();
	let snapshot: DesktopMediaPreferencesSnapshot | null = null;
	if (api) {
		try {
			await initializeDesktopMediaPreferences();
			const preference = toDevicePreference(deviceId);
			snapshot = await api.update(kind === 'input' ? {audioInput: preference} : {audioOutput: preference});
		} catch {
			// Keep the existing local VoiceSettings path usable if an older desktop
			// wrapper does not implement the preference bridge.
		}
	}
	if (kind === 'input') VoiceSettingsCommands.update({inputDeviceId: deviceId});
	else VoiceSettingsCommands.update({outputDeviceId: deviceId});
	return snapshot;
}

export async function updateDesktopScreenShareCodec(
	codec: DesktopScreenShareCodecPreference,
): Promise<DesktopMediaPreferencesSnapshot | null> {
	const api = getApi();
	let snapshot: DesktopMediaPreferencesSnapshot | null = null;
	if (api) {
		try {
			await initializeDesktopMediaPreferences();
			snapshot = await api.update({screenShareCodec: codec});
		} catch {
			// Keep the web client functional when running in an older desktop build.
		}
	}
	VoiceSettingsCommands.update({
		preferredScreenShareCodec: codec,
		...(codec === 'av1' ? {screenShareAv1OptIn: true} : {}),
		...(codec === 'h265' ? {screenShareHevcOptIn: true} : {}),
	});
	return snapshot;
}

export async function resetDesktopMediaPreference(
	key: DesktopMediaPreferenceKey,
): Promise<DesktopMediaPreferencesSnapshot | null> {
	const api = getApi();
	if (!api) return null;
	await initializeDesktopMediaPreferences();
	const snapshot = await api.reset(key);
	applySnapshot(snapshot);
	return snapshot;
}
